import React, { useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { Box, CircularProgress } from '@mui/material';
import { useSyncDocumentsWithDBStatus, SYNC_DOCUMENTS_WITH_DB_ACTIONS } from 'PdfDocumentGrid/hooks/SyncDocumentWithDbStatus.context';
import { InitialIsAckRequiredDocumentsState, useDocumentActionsStatus } from 'PdfDocumentGrid/hooks/DocumentActionsStatus.context';
import { getSettingByKey, ISynchronizationItemMeta, getAllSynchronizationItemsMeta } from 'utils/DexieDBManager';
import { getSyncDispatchItemsAsync, getSynchronizationItemsAsync } from 'PdfDocumentGrid/pdfDocuments.service';
import { styledPdfDocumentsList } from 'PdfDocumentGrid/PdfDocumentsList/stylesPdfDocumentsList';
import { getIsAckRequiredDocumentsCount } from 'PdfDocumentGrid/PdfDocumentsList/helpers';
import { useRedirectIfUnauthenticated } from 'utils/azureAD/useRedirectIfUnauthenticated';
import { APPLICABLE_STATE_NAME, ISetting } from 'utils/DexieDBManager/dexieDB.models';
import PdfDocumentsList from 'PdfDocumentGrid/PdfDocumentsList';
import { useSettings } from 'SettingsPage/Settings.Context';
import AlertMessage from 'Shared.Components/AlertMessage';
import { RESOURCES_KEYS } from 'utils/i18n/resources';
import { computeErrorMessage } from 'utils/utils';
import ErrorBoundary from 'utils/ErrorBoundary';
import { isPwa } from 'utils/pwa';

const CategoriesPage: React.FunctionComponent = () => {
  const hasRun = useRef(false);
  const { t: translate } = useTranslation();
  const { state: settings } = useSettings();
  const { accounts } = useRedirectIfUnauthenticated();
  const { state, dispatch } = useDocumentActionsStatus();
  const { state: syncDocsWithDbState, dispatch: syncDocsWithDbDispatch } = useSyncDocumentsWithDBStatus();
  
  const isOnline = navigator.onLine;
  const username = accounts[0]?.username;
  
  const lastSynchroDate = useLiveQuery<ISetting | undefined>(() => getSettingByKey('lastSynchroDate'));
  const synchronizationItemsFromDb =  useLiveQuery<ISynchronizationItemMeta[]>(() => getAllSynchronizationItemsMeta());

  const { data: getSynchronizationItemsData, isLoading: isGetSynchronizationItemsLoading } = useQuery(
    ["synchronizationItems", { isOnlyFav: false, username }],
    () => getSynchronizationItemsAsync(username, { hasToSyncDocuments: syncDocsWithDbState.SyncDocumentsWithDBEnabled }),
    {
      cacheTime: 0, 
      staleTime: 0,
      refetchOnReconnect: false,
      enabled: isOnline && Boolean(username),
      onSuccess: () => syncDocsWithDbDispatch({ type: SYNC_DOCUMENTS_WITH_DB_ACTIONS.DISABLE })
    }
  );

  useQuery(['syncDispatchItems', {username}], () => getSyncDispatchItemsAsync(username), {
    enabled: isOnline && settings.versionsApiHasBeenCalled && Boolean(lastSynchroDate?.value),
    refetchOnReconnect: false,
  });


  const filteredSynchronizationItemsFromDb = filterFavoriteSynchronizationItemMetas(synchronizationItemsFromDb || []);
  const synchronizationItemsFromServer = useMemo(() => getSynchronizationItemsData?.responseData?.items || [], [getSynchronizationItemsData?.responseData?.items]);

  const errorPageTitle = translate(RESOURCES_KEYS.ERROR_BOUNDARY_TITLE, {
    page: translate(RESOURCES_KEYS.MENU_CATEGORIES),
  });

  const isDataLoading = (isOnline && isGetSynchronizationItemsLoading && !synchronizationItemsFromServer.length) ||
                        (isPwa &&  !isOnline && !filteredSynchronizationItemsFromDb.length);

  React.useEffect(() => {
    if(isDataLoading) {
      return
    }

    if(hasRun.current) {
      return
    }

    if (isPwa && !isOnline) {
      filteredSynchronizationItemsFromDb.forEach((synchronization) => {
        const isCategoriesPageInitialized = state.categoriesIsAckRequiredDocumentsState.get(synchronization.id)?.isCategoriesPageInitialized;
        if (!isCategoriesPageInitialized) {
          dispatch({
            type: "initCategoriesIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isCategoriesPageInitialized: true,
              synchronizationId: synchronization.id,
              applicablesDocumentsCount: synchronization.categoriesApplicableDocumentsCount,
              documentsVersionCount: synchronization.categoriesDocumentVersionsCount,
              nextApplicablesDocumentsCount: synchronization.categoriesNextApplicableDocumentsCount,
              isAckRequiredApplicableDocumentsCount: synchronization.categoriesIsAckRequiredApplicableDocumentsCount,
              isAckRequiredNextApplicableDocumentsCount: synchronization.categoriesIsAckRequiredNextApplicableDocumentsCount,
            },
          });
        }

        const isStarredPageInitialized = state.starredIsAckRequiredDocumentsState.get(synchronization.id)?.isStarredPageInitialized;
        if(isStarredPageInitialized) {
          dispatch({
            type: "initStarredIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
              ...state.starredIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isStarredPageInitialized: false,
              isAcknowledgementPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
        
        const isAcknowledgementPageInitialized = state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id)?.isAcknowledgementPageInitialized;
        if (isAcknowledgementPageInitialized) {
          dispatch({
            type: "initAcknowledgementIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              ...state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isAcknowledgementPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
      });
      hasRun.current = true;
    } else if (isOnline && synchronizationItemsFromServer.length) {
      synchronizationItemsFromServer.forEach((synchronization) => {
        
        const isCategoriesPageInitialized = state.categoriesIsAckRequiredDocumentsState.get(synchronization.id)?.isCategoriesPageInitialized;
        if (!isCategoriesPageInitialized) {
          const applicablesCount = synchronization.applicables?.length ?? 0;
          const nextApplicablesCount = synchronization.nextApplicables?.length ?? 0;
          dispatch({
            type: "initCategoriesIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isCategoriesPageInitialized: true,
              synchronizationId: synchronization.id,
              applicablesDocumentsCount: applicablesCount,
              nextApplicablesDocumentsCount: nextApplicablesCount,
              documentsVersionCount: applicablesCount + nextApplicablesCount,
              isAckRequiredApplicableDocumentsCount: getIsAckRequiredDocumentsCount(synchronization.applicables),
              isAckRequiredNextApplicableDocumentsCount: getIsAckRequiredDocumentsCount(synchronization.nextApplicables),
            },
          });
        }

        const isStarredPageInitialized = state.starredIsAckRequiredDocumentsState.get(synchronization.id)?.isStarredPageInitialized;
        if(isStarredPageInitialized) {
          dispatch({
            type: "initStarredIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
              ...state.starredIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isStarredPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
        
        const isAcknowledgementPageInitialized = state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id)?.isAcknowledgementPageInitialized;
        if (isAcknowledgementPageInitialized) {
          dispatch({
            type: "initAcknowledgementIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              ...state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isAcknowledgementPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
        
        synchronization.applicables.forEach((documentVersion) => {
          const isInitialized =state.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized;
          if (!isInitialized) {
            dispatch({
              type: "init",
              initialDocumentStatus: {
                isAckRequired: documentVersion.uds.isAck,
                isStarredDocument: documentVersion.uds.isFav,
                isUnread: !Boolean(documentVersion.uds.lastRead),
                parentDocumentId: documentVersion.parentDocument.id,
                documentVersionType: APPLICABLE_STATE_NAME.APPLICABLE,
              },
            });
          }
        });
    
        synchronization.nextApplicables.forEach((documentVersion) => {
          const isInitialized = Boolean(state.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized);
          if (!isInitialized) {
            dispatch({
              type: "init",
              initialDocumentStatus: {
                isAckRequired: documentVersion.uds.isAck,
                isStarredDocument: documentVersion.uds.isFav,
                isUnread: !Boolean(documentVersion.uds.lastRead),
                parentDocumentId: documentVersion.parentDocument.id,
                documentVersionType: APPLICABLE_STATE_NAME.NEXT_APPLICABLE,
              },
            });
          }
        });
      });

      hasRun.current = true;
    }

  }, [dispatch, state, isOnline, filteredSynchronizationItemsFromDb, synchronizationItemsFromServer, isGetSynchronizationItemsLoading, isDataLoading]);



  return (
    <ErrorBoundary title={errorPageTitle}>
      <Box id='categoriesPage' sx={styledPdfDocumentsList}>
        {isDataLoading ? (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : getSynchronizationItemsData?.error ? (
          <AlertMessage title={<h2>{errorPageTitle}</h2>}>
            <h3>{`${translate(computeErrorMessage(getSynchronizationItemsData?.error))}, ${translate(RESOURCES_KEYS.PLEASE_CONTACT_SUPPORT_TEAM)}.`}</h3>
          </AlertMessage>
        ) : (
          <PdfDocumentsList
            synchronizationItemsFromServer={synchronizationItemsFromServer}
            synchronizationItemsFromDb={synchronizationItemsFromDb ?? []}
          />
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default CategoriesPage;

export const filterFavoriteSynchronizationItemMetas = (synchronizationItemMeta: ISynchronizationItemMeta[]) => synchronizationItemMeta.filter((item) => Boolean(item));
